import { useMemo } from 'react'

import { encodeRing } from 'utils/geoRing'
import { useSelector } from 'store/hooks'

/**
 * The drawn area (VRG-480) in wire form, or `undefined` when nothing is drawn.
 *
 * ## Why this is derived inside the hooks instead of threaded through each payload
 *
 * The area filter has to reach EVERY analytics request — the KPI tiles, the chart, the
 * ROI cards, the hero, and the map itself — because a page whose map is filtered and
 * whose numbers are not is a page telling two different stories at once.
 *
 * Those payloads are built as object literals at six or so call sites across
 * `AnalyticsPage`, `RoiHero`, `analyticsDisplayVariants` and `ScansByLocation`. Adding a
 * field to each is exactly the opt-in this codebase has already watched fail: §42's
 * referrer state was a prop that **2 of 37** call sites remembered to pass, and the
 * conclusion drawn there was that an opt-in 95% of call sites forget is a latent bug
 * rather than a convention. The fix taken there — derive it inside the shared hook, so
 * the behaviour is the DEFAULT and a new call site gets it for free — is the fix taken
 * here.
 *
 * The consequence worth stating plainly: **a new analytics card is area-filtered the
 * moment it uses one of these hooks**, with no per-card wiring and no per-card way to
 * forget.
 *
 * ## Its blast radius is exactly `location`'s, by construction
 *
 * Injected into precisely the hooks that already accept `location`, and no others. That
 * is a deliberate ceiling rather than an accident: it means the drawn area can never
 * narrow a figure the country filter would have left alone, so this feature introduces
 * no new question about which figures respond to a geo filter. The one place that
 * question is already live and already documented — the ROI hero, whose numerator
 * responds to geo filters while its Stripe-invoice denominator does not — behaves
 * identically for both, which is the honest outcome: the hero was already wrong in that
 * specific way for `location`, and matching it keeps the defect single rather than
 * doubling it.
 *
 * ## Cache correctness
 *
 * `useQuerySelect` decides whether to refetch by `JSON.stringify`-ing the params, and
 * `useAnalyticsScansMap` keys its redux slot the same way. Both therefore see the MERGED
 * object, because the merge happens before either — which is what makes clearing or
 * redrawing an area invalidate the cache rather than reading a stale entry drawn for a
 * different shape.
 */
export const useAnalyticsAreaParam = (): string | undefined => {
  const area = useSelector(state => state.analytics.filters.area)
  // Memoised on the RING, not on a params object — this is the one value in the merge
  // that is stable across renders, so it is the one worth holding.
  return useMemo(() => encodeRing(area), [area])
}

/**
 * Merge the drawn area into an analytics request payload.
 *
 * ⚠️ `area` is spread LAST and always in this position. `analyticsScansMapKey` is
 * `JSON.stringify` over the payload, and property order is load-bearing to it — two
 * cards asking the same question through different key orders would cache the same
 * request twice. One merge site is what makes the order unable to drift.
 *
 * Not memoised: callers build their params inline, so the object identity changes every
 * render regardless and a `[params]` dependency would never hold. `useQuerySelect`
 * already absorbs that with its own value comparison, which is the same reasoning
 * `useAnalyticsScansMap` records for not memoising its key.
 */
export const withAnalyticsArea = <T extends { area?: string }>(params: T, area: string | undefined): T =>
  area ? { ...params, area } : params

/**
 * The page-level SCAN OUTCOME filter (VERA-260), or `undefined` when nothing is selected.
 *
 * ## Why it is injected here rather than threaded through the payload
 *
 * Same reasoning as `useAnalyticsAreaParam` above, and it is not a coincidence: this is
 * the second page-global filter whose only sensible home is inside the shared hook. The
 * scans-map payload is built as an object literal inside `ScansByLocation` (and by any
 * future map variant, VRG-467), so an opt-in field is one every new card can forget —
 * §42's referrer prop reached 2 of 37 call sites. Deriving it inside the hook makes the
 * filter the DEFAULT: a new map card is scan-status-filtered the moment it uses the hook.
 *
 * ## Its blast radius is still narrower than `area`'s, but no longer by much
 *
 * Injected into `useAnalyticsScansMap`, `useAnalyticsStatistics` (the time chart) and
 * `useAnalyticsTotals` (the KPI tiles) — every hook whose scan figure is counted from the
 * `scans` table, whose `invalid` / `tampered` columns the outcome resolves from.
 *
 * It was scans-map alone until VERA-260 moved the chart's and the tiles' scan counts off
 * `certificate_action_logs`. That table has no outcome column and no row-level link back
 * to a scan, so while those two read it the filter was genuinely unexpressible there —
 * and the chart rendered a caveat line saying so. Both now read `scans`, the caveat is
 * gone, and one gesture on the scan-status filter narrows the tile, the chart and the map
 * together.
 *
 * ⚠️ NOT injected into `useAnalyticsRoi`, and that omission is load-bearing rather than
 * pending. The ROI query is mixed: `geographic_reach` and `invalid_scan_total` read
 * `scans`, but consumer touchpoints, repeat-scan rate and product lifespan still count
 * `certificate_action_logs`. Sending the filter would narrow some of one card's figures
 * and not others, which is worse than not narrowing any. `/roi` does not declare the
 * query prop either (see `ScanStatusesQueryProp` on the API side), so it would be
 * stripped by `whitelist: true` regardless — but the reason it must not be declared is
 * this one.
 */
export const useAnalyticsScanStatusParam = (): string[] | undefined => {
  const scanStatuses = useSelector(state => state.analytics.filters.scanStatuses)
  // Memoised on the redux array itself, which is stable between dispatches — the same
  // thing `useAnalyticsAreaParam` memoises on, for the same reason.
  return useMemo(
    () => (scanStatuses.length ? scanStatuses : undefined),
    [scanStatuses],
  )
}

/**
 * Merge the scan-outcome filter into an analytics request payload.
 *
 * ⚠️ Applied BEFORE `withAnalyticsArea`, always, so the merged key order is
 * `…params, scan_statuses, area`. `analyticsScansMapKey` is `JSON.stringify` over the
 * payload and property order is load-bearing to it — two cards asking the same question
 * through different key orders would cache one request twice. One merge site, one fixed
 * order, is what makes that unable to drift.
 *
 * ## ⚠️ A CALLER-SUPPLIED `scan_statuses` WINS, AND THAT IS A CORRECTNESS FIX (VERA-260)
 *
 * This used to spread unconditionally, so the page filter OVERWROTE anything the caller
 * had already put there. That was harmless while no caller set it, and became unsafe the
 * moment a map card PINNED an outcome as its identity: the "Invalid scans" map variant
 * asks for `scan_statuses: ['invalid']` because that is what the card IS, and under a page
 * filter of "Verified" the old spread would have replaced the pin and drawn verified pins
 * under a title reading "Invalid scans". A card silently plotting a different measurement
 * from the one it is named for is the worst failure this page has.
 *
 * A pinned card is responsible for INTERSECTING the page filter into its own value before
 * calling — `ScansByLocation` does, and refuses to fetch at all when the intersection is
 * empty, because an empty `scan_statuses` array means "no narrowing" to the server
 * (`buildScanOutcomeFilter` returns `''`), i.e. every scan. The precedence here only
 * decides who owns that arithmetic; it must be the card, which knows what it is named for.
 *
 * Cards with no pin are untouched: `params.scan_statuses` is `undefined` for every one of
 * them, so the page filter is injected exactly as before.
 */
export const withAnalyticsScanStatuses = <T extends { scan_statuses?: string[] }>(
  params: T,
  scanStatuses: string[] | undefined,
): T =>
  params.scan_statuses || !scanStatuses ? params : { ...params, scan_statuses: scanStatuses }
